/**
 * Commit-Reveal 密码学发牌协议（SPEC §3.2）。
 *
 * commit = SHA-256( canonicalize(card) || "|" || salt )
 *
 * - 教师端在配对开始时生成两张卡片与两个 256-bit 盐值并广播 hash；
 * - Deal 阶段只通过私有 RTCDataChannel 把明文卡片单播给本侧；
 * - Reveal 阶段广播 (card, salt)，任意客户端均可本地重算哈希验证。
 *
 * 使用 Web Crypto（浏览器与 Node 20+ 均原生提供），不引入第三方密码学库。
 */

export const SALT_BYTES = 32;
export const COMMIT_DOMAIN = 'zongheng-arena/deal/v1';

/**
 * 确定性 JSON 序列化：对象键按字典序递归排序，保证跨端哈希一致。
 * 不处理循环引用（卡片数据结构为纯 JSON，不存在循环）。
 */
export function canonicalize(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sortValue);
  const record = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) {
    const entry = record[key];
    if (entry === undefined) continue;
    sorted[key] = sortValue(entry);
  }
  return sorted;
}

/** 构造被哈希的消息体：domain 前缀 + 规范化卡片 + 盐值 */
export function commitmentMessage(card: unknown, salt: string): string {
  return `${COMMIT_DOMAIN}|${canonicalize(card)}|${salt}`;
}

export async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('当前运行环境缺少 Web Crypto (crypto.subtle)，无法执行 Commit-Reveal 协议');
  }
  const bytes = new TextEncoder().encode(text);
  const digest = await subtle.digest('SHA-256', bytes);
  return bytesToHex(new Uint8Array(digest));
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}

/** 生成 256-bit 随机盐（64 个十六进制字符） */
export function createSalt(): string {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) {
    throw new Error('当前运行环境缺少 crypto.getRandomValues');
  }
  return bytesToHex(cryptoApi.getRandomValues(new Uint8Array(SALT_BYTES)));
}

/** 计算卡片承诺哈希 */
export async function commitCard(card: unknown, salt: string): Promise<string> {
  return sha256Hex(commitmentMessage(card, salt));
}

/** 揭示阶段本地校验：重算哈希并与承诺比对 */
export async function verifyCommitment(
  card: unknown,
  salt: string,
  expectedHash: string,
): Promise<boolean> {
  const actual = await commitCard(card, salt);
  return timingSafeEqualHex(actual, expectedHash);
}

/** 十六进制字符串常量时间比较（避免哈希比对时的时序侧信道） */
export function timingSafeEqualHex(a: string, b: string): boolean {
  const left = a.toLowerCase();
  const right = b.toLowerCase();
  if (left.length !== right.length) return false;
  let diff = 0;
  for (let i = 0; i < left.length; i += 1) {
    diff |= left.charCodeAt(i) ^ right.charCodeAt(i);
  }
  return diff === 0;
}
